import { EmployeeHoverCard } from "@/components/employee/EmployeeHoverCard.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { PendingSpinnerTooltip } from "@/components/pending-change/PendingSpinnerTooltip.tsx";
import { HistoryTable } from "@/pages/employees/employee/tables/HistoryTable.tsx";
import { createHistoricalDateCell } from "@/pages/employees/employee/tables/table-cell-factory.utils.tsx";
import { EmployeeCustomTableRecordModal } from "@/pages/employees/employee/widgets/EmployeeCustomTableRecordModal.tsx";
import {
  useEmployeeCustomTableRowDelete,
  useEmployeeCustomTableSearch,
} from "@/queries/employees/use-employee-custom-table.query.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiCustomField,
  ApiFieldTypes,
  ApiFieldValueTypes,
  ApiTable,
} from "@/types/custom-field.types.ts";
import { isCompensationRollupField } from "@/utils/compensation-custom-field.utils.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import {
  ApiEmployee,
  ApiEmployeeCustomTablePendingRow,
  ApiEmployeeCustomTableRow,
} from "@/types/employees/employee.types.ts";
import { getEmployeePositionTitle } from "@/types/employees/employee-position.types.ts";
import {
  Button,
  ConfirmationModal,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Stack,
  Title,
  useModals,
  ExpandableText,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { type ReactNode, type RefObject, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface CustomTableProps {
  table: ApiTable;
  employee: ApiEmployee;
  tableScrollBoundaryRef?: RefObject<HTMLElement | null>;
}

type CustomTableRow =
  (ApiEmployeeCustomTableRow & { pending?: never }) | ApiEmployeeCustomTablePendingRow;

export function CustomTable({ table, employee, tableScrollBoundaryRef }: CustomTableProps) {
  const { t } = useTranslation();
  const {
    canManageCustomTableForEmployee,
    canViewCustomTableChangedByForEmployee,
    canViewCustomTableFieldForEmployee,
    canIEdit,
    isFeatureEnabled,
  } = usePermissions();
  const { openModal } = useModals();
  const { renderDate } = useFormatter();
  const { getFieldTitle, renderFieldDataInCell, renderPendingTooltipContent } =
    useCustomFieldsHook();
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useEmployeeCustomTableSearch(employee.id, table.id, {
    page_number: page,
  });

  const rowDelete = useEmployeeCustomTableRowDelete();

  const canManage = canManageCustomTableForEmployee(employee, table.id);
  const canEditTable =
    canIEdit(ApiPermission.manage_custom_fields) && isFeatureEnabled(ApiFeatureType.custom_fields);

  const displayData = useMemo<CustomTableRow[]>(
    () => [...(page === 1 ? (data?.pending_rows ?? []) : []), ...(data?.items ?? [])],
    [data, page]
  );

  const getTextClassName = useCallback(
    (row: CustomTableRow) => (!row.pending && row.is_current ? "text-title" : "text-secondary"),
    []
  );

  const handleAddRecord = () => {
    openModal(EmployeeCustomTableRecordModal, {
      employeeId: employee.id,
      table,
    });
  };

  const handleEdit = useCallback(
    (row: ApiEmployeeCustomTableRow) => {
      if (!canManage) return;

      openModal(EmployeeCustomTableRecordModal, {
        employeeId: employee.id,
        table,
        row,
      });
    },
    [employee.id, table, canManage, openModal]
  );

  const handleDeleteAction = useCallback(
    (row: ApiEmployeeCustomTableRow) => {
      if (!canManage) return;

      openModal(ConfirmationModal, {
        question: t("common:attention"),
        text: t("common:delete_confirm"),
        acceptTitle: t("common:delete"),
        onAccept: async () => {
          rowDelete.mutate({
            employeeId: employee.id,
            tableId: table.id,
            rowId: row.id,
          });
        },
      });
    },
    [employee.id, table.id, canManage, openModal, rowDelete, t]
  );

  const columnHelper = createColumnHelper<CustomTableRow>();

  const columns = useMemo<ColumnDef<CustomTableRow, unknown>[]>(() => {
    const wrapPendingCell = (content: ReactNode, row: CustomTableRow, isFirst = false) => (
      <Stack
        className="w-fit whitespace-nowrap text-secondary"
        direction="horizontal"
        items="center"
        gap="xs"
      >
        {isFirst && row.pending && (
          <PendingSpinnerTooltip
            tooltip={renderPendingTooltipContent(row.pending.datetime_created_at)}
          />
        )}
        {content}
      </Stack>
    );

    const inner: ColumnDef<CustomTableRow, unknown>[] = [];

    const historicalDateCell = createHistoricalDateCell<CustomTableRow>({
      getValue: (row) => (row.date_effective_from ? renderDate(row.date_effective_from) : null),
      getClassName: getTextClassName,
      getDate: (row) => row.date_effective_from,
    });

    table.table_fields
      .filter((field) => {
        if (field.type === ApiFieldTypes.changed_by) {
          return canViewCustomTableChangedByForEmployee(employee, table.id);
        }
        if (field.type === ApiFieldTypes.effective_from) {
          return true;
        }
        return canViewCustomTableFieldForEmployee(employee, table.id, field.slug);
      })
      .forEach((field, fieldIndex) => {
        if (field.type === ApiFieldTypes.effective_from) {
          inner.push(
            columnHelper.display({
              header: getFieldTitle(field),
              cell: (cell) => {
                const row = cell.row.original;
                if (row.pending) {
                  return wrapPendingCell(
                    row.date_effective_from ? renderDate(row.date_effective_from) : "-",
                    row,
                    true
                  );
                }
                return historicalDateCell(cell);
              },
              id: field.slug,
              ...ColumnSizes.historicalDate,
            })
          );
          return;
        }

        if (field.type === ApiFieldTypes.changed_by) {
          inner.push(
            columnHelper.display({
              header: getFieldTitle(field),
              cell: ({ row }) =>
                row.original.changed_by ? (
                  <EmployeeHoverCard
                    employee={row.original.changed_by}
                    position={getEmployeePositionTitle(row.original.changed_by)}
                  >
                    <EmployeeItem
                      employee={row.original.changed_by}
                      showPosition={false}
                      size="sm"
                    />
                  </EmployeeHoverCard>
                ) : null,
              id: field.slug,
              ...ColumnSizes.standard,
            })
          );
          return;
        }

        inner.push(
          columnHelper.display({
            header: isCompensationRollupField(field.type) ? getFieldTitle(field) : field.title,
            cell: (cell) => {
              const row = cell.row.original;
              const tableCell = row.cells.find((c) => c.field.slug === field.slug);
              const isCompensationRollup = isCompensationRollupField(field.type);
              if (!tableCell && !isCompensationRollup) return "-";

              const customFields = row.cells.map(
                (rowCell) => ({ field: rowCell.field, value: rowCell.value }) as ApiCustomField
              );

              if (row.pending) {
                return wrapPendingCell(
                  renderFieldDataInCell(field, customFields, {
                    currencyTextClassName: "text-secondary",
                  }),
                  row,
                  fieldIndex === 0
                );
              }

              const textClassName = row.is_current ? "text-title" : "text-secondary";

              if (field.field_type === ApiFieldValueTypes.textarea) {
                return (
                  <ExpandableText className={twMerge(textClassName, "whitespace-pre-wrap")}>
                    {renderFieldDataInCell(field, customFields)}
                  </ExpandableText>
                );
              }

              if (field.field_type === ApiFieldValueTypes.currency) {
                return renderFieldDataInCell(field, customFields, {
                  currencyTextClassName: textClassName,
                });
              }

              return (
                <span className={textClassName}>
                  {renderFieldDataInCell(field, customFields, {
                    currencyTextClassName: textClassName,
                  })}
                </span>
              );
            },
            id: field.slug,
            ...(field.field_type === ApiFieldValueTypes.textarea ? ColumnSizes.wide : {}),
          })
        );
      });

    return inner;
  }, [
    table,
    employee,
    canViewCustomTableFieldForEmployee,
    canViewCustomTableChangedByForEmployee,
    renderFieldDataInCell,
    renderPendingTooltipContent,
    renderDate,
    getTextClassName,
    getFieldTitle,
    columnHelper,
  ]);

  const renderRowAction = useCallback(
    (tableRow: Row<CustomTableRow>) => {
      const row = tableRow.original;
      if (row.pending) return null;
      return (
        <DropdownMenu>
          <DropdownMenuItem onClick={() => handleEdit(row)} title={t("common:edit")} />
          <DropdownMenuItem
            onClick={() => handleDeleteAction(row)}
            variant="menuDanger"
            title={t("common:delete") + "..."}
          />
        </DropdownMenu>
      );
    },
    [handleEdit, handleDeleteAction, t]
  );

  const displayCount = data?.metadata?.total_items || 0;

  return (
    <Stack gap="lg">
      <Stack
        className="justify-between"
        direction="horizontal"
        items="center"
        gap="md"
        data-employee-table-heading
      >
        <Title
          header={table.title}
          size="lg"
          badge={displayCount > 1 ? `(${displayCount})` : undefined}
          badgeVariant={"secondary"}
          actionIcon={<FontAwesomeIcon icon="fa-light fa-chevron-right" />}
          to={`/employees/table/${table.slug}/rows`}
          target="_blank"
        />
        <DropdownMenu
          trigger={
            <Button
              variant="secondary"
              rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-down" />}
            >
              {t("common:actions")}
            </Button>
          }
        >
          {canManage && (
            <DropdownMenuItem
              onClick={handleAddRecord}
              leftIcon={<FontAwesomeIcon icon="fa-light fa-plus" />}
              title={t("common:add_record")}
            />
          )}
          <DropdownMenuItem
            to={`/employees/table/${table.slug}/rows`}
            target="_blank"
            leftIcon={<FontAwesomeIcon icon="fa-light fa-table" />}
            title={t("custom_fields:view_all_data")}
          />
          {canEditTable && (
            <DropdownMenuItem
              to={`/settings/custom-fields/table/${table.id}`}
              target="_blank"
              leftIcon={<FontAwesomeIcon icon="fa-light fa-gear" />}
              title={t("custom_fields:edit_table")}
            />
          )}
        </DropdownMenu>
      </Stack>

      <HistoryTable<CustomTableRow>
        historyItems={displayData}
        columns={columns}
        canViewHistory={true}
        isLoading={isLoading}
        isFetching={isFetching || rowDelete.isPending}
        pagination={{
          metadata: data?.metadata,
          currentPage: page,
          onPageChange: setPage,
        }}
        emptyStateTitle={t("common:no_records_yet")}
        scrollLayout={tableScrollBoundaryRef ? "notion" : undefined}
        scrollBoundaryRef={tableScrollBoundaryRef}
        stickyLastColumn={false}
        renderRowAction={canManage ? renderRowAction : undefined}
      />
    </Stack>
  );
}
